/**
 * Announcement management: list all announcements (vendor and platform), edit any.
 * Admin-only: uses useAdmin() and redirects non-admins.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAdmin } from '@/hooks/useAdmin'
import { format } from 'date-fns'
import CreateAnnouncementModal, { type AnnouncementRow } from '@/components/CreateAnnouncementModal'

const ADMIN_ANNOUNCEMENTS_QUERY_KEY = ['admin-announcements'] as const

interface AnnouncementWithVendor extends AnnouncementRow {
  vendors: { vendor_name: string } | null
}

export default function AdminAnnouncementsPage() {
  const navigate = useNavigate()
  const isAdmin = useAdmin()
  const queryClient = useQueryClient()
  const [editAnnouncement, setEditAnnouncement] = useState<AnnouncementRow | null>(null)

  const deleteAnnouncementMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('announcements').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] })
      queryClient.invalidateQueries({ queryKey: ['admin-announcements'] })
    },
    onError: (err: Error) => {
      alert(`Failed to delete announcement: ${err.message}`)
    },
  })

  const handleDeleteAnnouncement = (id: string) => {
    if (!window.confirm('Delete this announcement? This cannot be undone.')) return
    deleteAnnouncementMutation.mutate(id)
  }

  const { data: announcements = [], isLoading } = useQuery({
    queryKey: ADMIN_ANNOUNCEMENTS_QUERY_KEY,
    queryFn: async (): Promise<AnnouncementWithVendor[]> => {
      const { data, error } = await supabase
        .from('announcements')
        .select('id, vendor_id, announcement_text, expiry_days, image_url, created_at, expires_at, vendors(vendor_name)')
        .order('created_at', { ascending: false })

      if (error) {
        console.warn('Error fetching announcements:', error)
        return []
      }
      return (data ?? []) as AnnouncementWithVendor[]
    },
    enabled: isAdmin === true,
  })

  if (isAdmin === false) {
    navigate('/', { replace: true })
    return null
  }

  const sourceLabel = (a: AnnouncementWithVendor) => a.vendors?.vendor_name ?? 'Platform'
  const isExpired = (expiresAt: string | undefined) => expiresAt && new Date(expiresAt) <= new Date()

  return (
    <div className="max-w-4xl">
      <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
        Announcement management
      </h1>
      <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
        Edit or delete any announcement (vendor or platform). Changes apply immediately on the homepage.
      </p>

      {isLoading ? (
        <p role="status" style={{ color: 'var(--text-secondary)' }}>
          Loading…
        </p>
      ) : announcements.length === 0 ? (
        <p style={{ color: 'var(--text-secondary)' }}>No announcements yet.</p>
      ) : (
        <div
          className="rounded-lg border-2 overflow-hidden"
          style={{ borderColor: 'var(--bg-tertiary)', backgroundColor: 'var(--bg-secondary)' }}
        >
          <table className="w-full" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--bg-tertiary)' }}>
                <th scope="col" className="text-left p-3 font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Source
                </th>
                <th scope="col" className="text-left p-3 font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Created
                </th>
                <th scope="col" className="text-left p-3 font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Expires
                </th>
                <th scope="col" className="text-left p-3 font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Text
                </th>
                <th scope="col" className="text-right p-3 font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {announcements.map((a) => (
                <tr
                  key={a.id}
                  style={{
                    borderBottom: '1px solid var(--bg-tertiary)',
                    backgroundColor: isExpired(a.expires_at) ? 'var(--bg-primary)' : undefined,
                  }}
                >
                  <td className="p-3" style={{ color: 'var(--text-primary)' }}>
                    {sourceLabel(a)}
                  </td>
                  <td className="p-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {a.created_at ? format(new Date(a.created_at), 'dd/MM/yyyy') : '—'}
                  </td>
                  <td className="p-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {a.expires_at ? format(new Date(a.expires_at), 'dd/MM/yyyy') : '—'}
                    {isExpired(a.expires_at) && (
                      <span className="ml-1" style={{ color: 'var(--color-error)' }}>(expired)</span>
                    )}
                  </td>
                  <td className="p-3 max-w-xs">
                    <p
                      className="truncate text-sm"
                      style={{ color: 'var(--text-primary)' }}
                      title={a.announcement_text}
                    >
                      {a.announcement_text}
                    </p>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditAnnouncement(a)}
                        className="px-3 py-1.5 text-sm font-medium rounded"
                        style={{
                          backgroundColor: 'var(--bg-interactive)',
                          color: 'var(--text-inverse)',
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteAnnouncement(a.id)}
                        disabled={deleteAnnouncementMutation.isPending}
                        className="px-3 py-1.5 text-sm font-medium rounded"
                        style={{
                          backgroundColor: 'var(--color-error)',
                          color: 'var(--text-inverse)',
                          opacity: deleteAnnouncementMutation.isPending ? 0.6 : 1,
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editAnnouncement && (
        <CreateAnnouncementModal
          vendorId={editAnnouncement.vendor_id}
          onClose={() => setEditAnnouncement(null)}
          onSuccess={() => setEditAnnouncement(null)}
          existing={editAnnouncement}
        />
      )}
    </div>
  )
}
